import { useCallback, useRef } from 'react';

import type { PurchaseSource } from '@sunflower-games/analytics-events';

import { useFlag } from '../services/launchDarkly';
import type { IPackage } from '../stores/app/types';
import type { CoinType } from '../stores/cointype/CoinTypeStore';
import type { BundleItem, MetaGameTokenPayload } from '../stores/rewards/types';
import { useStores } from '../stores/useStores';

import { usePhoneVerifyDialog } from './usePhoneVerifyDialog';
import { usePurchaseDepositLimitsPreflight } from './usePurchaseDepositLimitsPreflight';

export type OpenOrderData = IPackage & {
  minigameId?: number;
  extraData?: any;
  metagamesTokensAmount?: number;
  currencyName?: string;
  coinType?: CoinType;
  metagamesRewardsPayloads?: MetaGameTokenPayload[];
  bundleItems?: BundleItem[];
};

export function useOpenOrder() {
  const { userStore, appStore, payRedeemStore, uiStore, eventTriggerStore } = useStores();
  const initialCountryRef = useRef<string | undefined>(undefined);

  const { openVerifyPhoneDialog } = usePhoneVerifyDialog();
  const isPurchasePhoneVerificationEnabled = useFlag('purchase_phone_verification_enabled', true);
  const { ensurePurchaseDepositAllowed } = usePurchaseDepositLimitsPreflight();

  const openOrder = useCallback(
    (args: {
      data: OpenOrderData;
      handleClosePhoneVerification?: VoidFunction;
      isMiniStore?: boolean;
      isStoreItem?: boolean;
      onClose?: VoidFunction;
      purchaseSource?: PurchaseSource;
      onSuccessfulPurchaseFlowEnd?: VoidFunction;
    }) => {
      const {
        data,
        handleClosePhoneVerification,
        isMiniStore,
        isStoreItem,
        onClose,
        purchaseSource,
        onSuccessfulPurchaseFlowEnd,
      } = args;

      const purchasePackageData = {
        ...data,
        eventTriggerData: data.eventTriggerData,
      };
      payRedeemStore.setAfterSuccessfulPurchaseCallback(onSuccessfulPurchaseFlowEnd || null);
      appStore.setCurrentOrderPackage(purchasePackageData);

      sessionStorage.setItem('purchaseActiveEvent', JSON.stringify(purchasePackageData));
      sessionStorage.setItem('purchaseSource', purchaseSource || '');
      initialCountryRef.current = userStore.profile?.cleanGeoData?.countryCode;

      const continueFlow = async () => {
        if (userStore.profile?.cleanGeoData?.countryCode !== initialCountryRef.current) {
          if (isMiniStore) {
            uiStore.setIsVisibleMiniStore(true);
          } else {
            uiStore.setOpenFullPageDialog('store');
          }
        } else {
          const allowed = await ensurePurchaseDepositAllowed(data.packageId);
          if (!allowed) {
            return;
          }
          appStore.handleOpenOrder(data.packageId, isMiniStore, onClose, isStoreItem);
        }
      };

      if (isPurchasePhoneVerificationEnabled && (!userStore.profile?.phone || !userStore.profile?.phoneVerified)) {
        if (eventTriggerStore.isPopupOpen) {
          void eventTriggerStore.closePopup();
        }

        openVerifyPhoneDialog(() => void continueFlow(), {
          ...(handleClosePhoneVerification && { beforeClose: handleClosePhoneVerification }),
        });
      } else {
        void continueFlow();
      }
    },
    [
      appStore,
      ensurePurchaseDepositAllowed,
      isPurchasePhoneVerificationEnabled,
      openVerifyPhoneDialog,
      payRedeemStore,
      uiStore,
      userStore,
      eventTriggerStore,
    ],
  );

  return {
    openOrder,
  };
}
